import { httpClient } from './httpClient';

export interface Price {
  id: string;
  unit_amount: number;
  currency: string;
  interval: string;
  interval_count: number;
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  features: string[];
  price: Price;
  metadata: Record<string, string>;
}

export interface Subscription {
  id: number;
  stripeSubscriptionId?: string; // Stripe subscription ID
  stripeCustomerId?: string; // Stripe customer ID
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  plan: {
    id: string;
    name: string;
    amount: number;
    currency: string;
    interval: string;
  };
}

export interface Invoice {
  id: string;
  number: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  hostedInvoiceUrl: string;
}

export const subscriptionService = {
  async getCurrentSubscription(): Promise<Subscription | null> {
    try {      
      try {
        // First try the tenant-specific endpoint
        const response = await httpClient.get<any>('/api/subscriptions/current');
        
        // Check if the response has the expected structure
        if (response && response.success === true) {
          if (response.data) {
            return response.data;
          } else {
            // This is a valid response with null data (no subscription)
            return null;
          }
        } else {
          console.log('Response did not contain valid subscription data:', response);
        }
      } catch (error) {
        const primaryError = error as any;
        console.error('Error from primary endpoint:', primaryError);
        
        // Log detailed error information
        if (primaryError.response) {
          console.error('Error status:', primaryError.response.status);
          console.error('Error data:', primaryError.response.data);
          
          // If we get a 404 with a message about no subscription found, this is expected for new tenants
          if (primaryError.response.status === 404) {
            return null;
          }
        }
      }
      
      // Only fall back to the stripe-specific endpoint if we didn't get a clear 404
      try {
        const fallbackResponse = await httpClient.get<any>('/api/subscriptions/stripe/subscriptions');
        
        if (fallbackResponse && fallbackResponse.success === true) {
          if (fallbackResponse.data) {
            return fallbackResponse.data;
          } else {
            // This is a valid response with null data (no subscription)
            return null;
          }
        } else {
          console.log('Fallback response did not contain valid subscription data');
        }
      } catch (error) {
        const fallbackError = error as any;
        console.error('Error from fallback endpoint:', fallbackError);
        
        // Log detailed error information
        if (fallbackError.response) {
          console.error('Fallback error status:', fallbackError.response.status);
          console.error('Fallback error data:', fallbackError.response.data);
        }
      }
      
      return null;
    } catch (error) {
      console.error('Unexpected error in getCurrentSubscription:', error);
      throw error; // Propagate the error to the component for handling
    }
  },

  async getProducts(): Promise<Product[]> {
    try {
      const response = await httpClient.get<{ success: boolean; data: Product[] }>('/api/subscriptions/stripe/products');
      // Check if the response has the expected structure and return the data field
      if (response && response.success && 'data' in response) {
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  },

  async createSubscription(priceId: string): Promise<{ url?: string; requiresAuth?: boolean; redirectUrl?: string }> {
    try {      
      const response = await httpClient.post<{ 
        success: boolean; 
        url?: string; 
        requiresAuth?: boolean; 
        redirectUrl?: string 
      }>('/api/subscriptions/stripe/checkout-session', { priceId });
      
      // Check if the response has the expected structure
      if (response && response.success) {
        // Handle authentication redirect case
        if (response.requiresAuth && response.redirectUrl) {
          return { 
            requiresAuth: true, 
            redirectUrl: response.redirectUrl 
          };
        }
        // Handle successful checkout URL case
        if ('url' in response) {
          return { url: response.url };
        }
      }
      
      return { url: undefined };
    } catch (error) {
      console.error('Error creating subscription:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      // Return undefined URL instead of throwing error
      return { url: undefined };
    }
  },

  async updateSubscription(subscriptionId: number | string, priceId: string): Promise<void> {
    try {
      await httpClient.put<{ success: boolean }>('/api/subscriptions/stripe/subscriptions', { subscriptionId, priceId });
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  },

  async cancelSubscription(subscriptionId?: number | string, options?: { headers?: Record<string, string> }): Promise<void> {
    try {
      
      // If no subscription ID is provided, use the tenant-specific endpoint
      if (!subscriptionId) {
        await httpClient.post<{ success: boolean }>('/api/subscriptions/cancel', undefined, options);
        return;
      }
      
      // Otherwise use the subscription-specific endpoint
      await httpClient.post<{ success: boolean }>(`/api/subscriptions/${subscriptionId}/cancel`, undefined, options);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  },

  async reactivateSubscription(subscriptionId: number): Promise<void> {
    try {
      await httpClient.post<{ success: boolean }>(`/api/subscriptions/stripe/subscriptions/${subscriptionId}/reactivate`);
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  },

  async getInvoices(): Promise<Invoice[]> {
    try {
      // Get the tenant ID to ensure we're only getting invoices for the current tenant
      const tenantId = localStorage.getItem('tenantId');

      if (!tenantId) {
        console.warn('No tenant ID found in localStorage, cannot fetch tenant-specific invoices');
        return [];
      }

      // Explicitly add the tenant ID to the headers for this request
      const options = {
        headers: {
          'X-Tenant-ID': tenantId
        }
      };

      // Make the request with tenant ID in headers
      // const response = await httpClient.get<{ success: boolean; data: Invoice[] }>(
      //   '/api/subscriptions/stripe/invoices',
      //   options
      // );

      const response = await httpClient.get<{ success: boolean; data: Invoice[] }>(
        '/api/subscriptions/v2/stripe/invoices',
        options
      );

      if (response && response.success && response.data) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return [];
    }
  },

  async createPortalSession(): Promise<{ url?: string }> {
    try {
      const response = await httpClient.post<{ success: boolean; url?: string }>('/api/subscriptions/stripe/portal');
      return { url: response.url };
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }
};
